/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import {
  AdBreakInfo,
  AdContentTimePosition,
  AdInfo,
  AdVerification,
  Player,
  VASTProperties,
  VerificationResource,
  IndustryIcon,
  IndustryIconProperty,
  IconClickFallbackImage,
  Extensions,
  IconClickFallbackImageProperty,
} from '@quickplay/player-interface';

import { YospaceVastProperties } from './YospaceVastProperties';

/**
 * The name of a verification resource.
 */
const JS_RESOURCE_NAME = 'JavaScriptResource';
/**
 * The attribute's key of a verification resource.
 */
const JS_RESOURCE_ATTR_KEY_API_FRAMEWORK = 'apiFramework';
/**
 * The attribute's value of a verification resource.
 */
const JS_RESOURCE_ATTR_VAL_OMID = 'omid';
/**
 * Image Mime type
 */
const IMAGE_PNG = 'image/png';

/**
 * Creates a new instance of AdInfo.
 * @param advert the advert
 * @param player the content player
 * @param activeAdBreakInfo the advert break that contains this advert
 */

export function createAdInfo(advert: any, player: any, activeAdBreakInfo?: AdBreakInfo): AdInfo {
  return {
    get adID(): string {
      return advert.getIdentifier() || ' ';
    },

    get sequence(): number {
      return advert.getSequence();
    },

    get adStartTimeOffsetMs(): number {
      return advert.getStart();
    },

    get durationMs(): number {
      return advert.getDuration();
    },

    get remainingTimeMs(): number {
      return advert.getRemainingTime(player.currentTime * 1000);
    },

    get isSkippable(): boolean {
      return advert.getSkipOffset() > -1;
    },

    get skipOffsetMs(): number {
      return advert.getSkipOffset();
    },

    get isFiller(): boolean {
      return advert.isFiller();
    },

    get adBreakInfo(): AdBreakInfo | undefined {
      return activeAdBreakInfo;
    },
    get vastProperties(): VASTProperties | undefined {
      return advert ? getVastProperties(advert) : undefined;
    },
    delivery: { technology: 'server-side', stitcher: 'Yospace' },
  };
}

/**
 * Creates a new instance of AdBreakInfo.
 * @param adBreak the advert break
 * @param timePosition the content time position
 * @param adBreakIndex the advert break index
 * @param player the content player
 */
export function createAdBreakInfo(
  adBreak: any,
  timePosition: AdContentTimePosition,
  adBreakIndex: number,
  player: Player,
): AdBreakInfo {
  return {
    get adBreakID(): string {
      return adBreak.getIdentifier() || ' ';
    },

    get contentTimePosition(): AdContentTimePosition {
      return timePosition;
    },

    get adSequencePosition(): number {
      return 1;
    },

    get adBreakStartTimeOffsetMs(): number {
      return adBreak.getStart();
    },

    get remainingTimeMs(): number {
      return adBreak.getRemainingTime(player.currentTime * 1000);
    },

    get durationMs(): number {
      return adBreak.getDuration();
    },

    get totalAds(): number {
      return adBreak.getAdverts().length;
    },

    get adBreakIndex(): number {
      return adBreakIndex;
    },
  };
}

/**
 * Searches for a verification resource called 'JavaScript' with attribute['apiFramework'] that equals 'omid'
 * and returns the verification resource's value.
 * @param adVerification the advert verification
 */
export function findJavaScriptResourceURL(adVerification: AdVerification): string | null {
  const verificationResources = adVerification.verificationResources
    .filter((verificationResource: VerificationResource) => verificationResource.name === JS_RESOURCE_NAME)
    .filter((verificationResource: VerificationResource) =>
      verificationResource.attributes.has(JS_RESOURCE_ATTR_KEY_API_FRAMEWORK),
    )
    .filter(
      (verificationResource: VerificationResource) =>
        verificationResource.attributes.get(JS_RESOURCE_ATTR_KEY_API_FRAMEWORK) === JS_RESOURCE_ATTR_VAL_OMID,
    );

  if (verificationResources.length) {
    return verificationResources[0].value;
  }

  return null;
}

function getVastProperties(advert: any): VASTProperties {
  const linearCreative = advert.getLinearCreative();
  const vastProperties: VASTProperties = {
    creativeId: linearCreative ? linearCreative.getCreativeIdentifier() : undefined,
  };

  const yoVastProperties = advert.properties;
  if (!yoVastProperties) {
    return vastProperties;
  }

  Object.keys(yoVastProperties).forEach(property => {
    switch (yoVastProperties[property].name) {
      case YospaceVastProperties.ADSYSTEM: {
        vastProperties.adSystem = yoVastProperties[property].value;
        break;
      }
      case YospaceVastProperties.ADTITLE: {
        vastProperties.adTitle = yoVastProperties[property].value;
        break;
      }
      case YospaceVastProperties.ADSERVINGID: {
        vastProperties.adServingId = yoVastProperties[property].value;
        break;
      }
      case YospaceVastProperties.CATEGORY: {
        vastProperties.category = yoVastProperties[property].value;
        break;
      }
      case YospaceVastProperties.DESCRIPTION: {
        vastProperties.description = yoVastProperties[property].value;
        break;
      }
      case YospaceVastProperties.ADVERTISER: {
        vastProperties.advertiser = yoVastProperties[property].value;
        break;
      }
      case YospaceVastProperties.SURVEY: {
        vastProperties.survey = yoVastProperties[property].value;
        break;
      }
      case YospaceVastProperties.PRICING: {
        vastProperties.pricing = yoVastProperties[property].value;
        break;
      }
      case YospaceVastProperties.EXPIRES: {
        vastProperties.expires = yoVastProperties[property].value;
        break;
      }
    }
  });
  vastProperties.industryIcons = buildIndustryIconMetadata(advert);
  vastProperties.extensions = buildVastExtensions(advert);
  return vastProperties;
}

function buildIndustryIconMetadata(advert: any): IndustryIcon[] | undefined {
  const adIndustryIcons: IndustryIcon[] = [];
  const industryIcons: any = advert.getIndustryIcons();
  if (industryIcons === undefined || industryIcons.length === 0) {
    return undefined;
  }

  for (const industryIcon of industryIcons) {
    const adIndustryIcon: IndustryIcon = getAdIndustryIcon(industryIcon);
    adIndustryIcons.push(adIndustryIcon);
  }
  return adIndustryIcons;
}

function getAdIndustryIcon(industryIcon: any): IndustryIcon {
  return {
    program: industryIcon.getProperty(IndustryIconProperty.PROGRAM)
      ? industryIcon.getProperty(IndustryIconProperty.PROGRAM).value
      : undefined,
    width: industryIcon.getProperty(IndustryIconProperty.WIDTH)
      ? industryIcon.getProperty(IndustryIconProperty.WIDTH).value
      : undefined,
    height: industryIcon.getProperty(IndustryIconProperty.HEIGHT)
      ? industryIcon.getProperty(IndustryIconProperty.HEIGHT).value
      : undefined,
    xPosition: industryIcon.getProperty(IndustryIconProperty.XPOSITION)
      ? industryIcon.getProperty(IndustryIconProperty.XPOSITION).value
      : undefined,
    yPosition: industryIcon.getProperty(IndustryIconProperty.YPOSITION)
      ? industryIcon.getProperty(IndustryIconProperty.YPOSITION).value
      : undefined,
    duration: industryIcon.getProperty(IndustryIconProperty.DURATION)
      ? industryIcon.getProperty(IndustryIconProperty.DURATION).value
      : undefined,
    offset: industryIcon.getProperty(IndustryIconProperty.OFFSET)
      ? industryIcon.getProperty(IndustryIconProperty.OFFSET).value
      : undefined,
    apiFramework: industryIcon.getProperty(IndustryIconProperty.APIFRAMEWORK)
      ? industryIcon.getProperty(IndustryIconProperty.APIFRAMEWORK).value
      : undefined,
    clickThroughURL: industryIcon.getClickThroughUrl() ?? undefined,
    altText: industryIcon.getProperty(IndustryIconProperty.ALTTEXT)
      ? industryIcon.getProperty(IndustryIconProperty.ALTTEXT).value
      : undefined,
    hoverText: industryIcon.getProperty(IndustryIconProperty.HOVERTEXT)
      ? industryIcon.getProperty(IndustryIconProperty.HOVERTEXT).value
      : undefined,
    imageURL: getImageURL(industryIcon.getResources()),
    fallbackImages: getFallbackImages(industryIcon),
  };
}

function getImageURL(resources: any): string | undefined {
  let imageURL: string | undefined;
  if (resources === undefined && resources.size === 0) {
    return undefined;
  }
  for (const [, resource] of resources) {
    const creativeType: string | null = resource && resource.getCreativeType();
    if (creativeType !== null && creativeType === IMAGE_PNG) {
      imageURL = resource.getStringData();
    }
  }
  return imageURL;
}

function getFallbackImages(industryIcon: any): IconClickFallbackImage[] | undefined {
  const clickFallbackImages: IconClickFallbackImage[] = [];
  if (industryIcon.getClickFallbackImages() === undefined || industryIcon.getClickFallbackImages().length === 0) {
    return undefined;
  }
  for (const clickFallbackImage of industryIcon.getClickFallbackImages()) {
    const fallbackImage: IconClickFallbackImage = {
      width: clickFallbackImage.getProperty(IndustryIconProperty.WIDTH)
        ? clickFallbackImage.getProperty(IndustryIconProperty.WIDTH).value
        : undefined,
      height: clickFallbackImage.getProperty(IndustryIconProperty.HEIGHT)
        ? clickFallbackImage.getProperty(IndustryIconProperty.HEIGHT).value
        : undefined,
      altText: clickFallbackImage.getAltText() ?? undefined,
      imageURL: getFallbackImageURL(clickFallbackImage),
    };
    clickFallbackImages.push(fallbackImage);
  }

  return clickFallbackImages;
}

function getFallbackImageURL(clickFallbackImage: any): string | undefined {
  const resource = clickFallbackImage.getResource();
  const creativeType: string | null = resource && resource.getCreativeType();
  if (creativeType === null || creativeType !== IMAGE_PNG) {
    return undefined;
  }
  return resource.getStringData();
}

function buildVastExtensions(advert: any): Extensions | undefined {
  const vastExtensions: Extensions = {};
  const extensions: any = advert.getExtensions();
  if (extensions === undefined || extensions.length === 0) {
    return undefined;
  }

  const extensionArray = extensions.getChildren();

  if (extensionArray === undefined || extensionArray.length === 0) {
    return undefined;
  }

  for (const extension of extensionArray) {
    if (
      extension.getAttribute(IconClickFallbackImageProperty.TYPE) ===
      IconClickFallbackImageProperty.ICONCLICKFALLBACKIMAGES
    ) {
      vastExtensions.clickFallbackImages = buildIconClickFallbackImageFromExtension(extension);
    }
  }
  return vastExtensions;
}

function buildIconClickFallbackImageFromExtension(extension: any): IconClickFallbackImage[] | undefined {
  const iconClickFallbackImages: IconClickFallbackImage[] = [];
  const extensionchildren = extension.getChildren();
  if (extensionchildren === undefined || extensionchildren.length === 0) {
    return undefined;
  }

  for (const child of extensionchildren) {
    let iconClickFallbackImage: IconClickFallbackImage = {};
    const iconClickFallbackImageChildren = child.getChildren();
    if (iconClickFallbackImageChildren !== undefined && iconClickFallbackImageChildren.length !== 0) {
      const program = child.getAttribute(IndustryIconProperty.PROGRAM);
      for (const iconClickFallbackImageChild of iconClickFallbackImageChildren) {
        iconClickFallbackImage = buildIconClickFallbackImage(program, iconClickFallbackImageChild);
        iconClickFallbackImages.push(iconClickFallbackImage);
      }
    }
  }

  return iconClickFallbackImages;
}

function buildIconClickFallbackImage(program: any, iconClickFallbackImageChild: any): IconClickFallbackImage {
  const iconClickFallbackImage: IconClickFallbackImage = {};
  iconClickFallbackImage.program = program;
  iconClickFallbackImage.height = iconClickFallbackImageChild.getAttribute(IconClickFallbackImageProperty.HEIGHT);
  iconClickFallbackImage.width = iconClickFallbackImageChild.getAttribute(IconClickFallbackImageProperty.WIDTH);
  if (
    iconClickFallbackImageChild.getChildren() !== undefined &&
    iconClickFallbackImageChild.getChildren().length !== 0
  ) {
    for (const child of iconClickFallbackImageChild.getChildren()) {
      const name = child.getName();
      switch (name) {
        case IconClickFallbackImageProperty.ALTTEXT:
          iconClickFallbackImage.altText = child.getInnerText();
          break;
        case IconClickFallbackImageProperty.STATICRESOURCE:
          iconClickFallbackImage.imageURL = child.getInnerText();
          break;
      }
    }
  }
  return iconClickFallbackImage;
}
